// Relatório por aluno: busca por nome ou telefone e ficha com cursos e respostas.
import React, { useMemo, useState } from 'react';
import { FiArrowLeft, FiCheck, FiDownload, FiSearch, FiX } from 'react-icons/fi';
import Carregando from '../Carregando';
import { Medidor, SeloStatus } from './Graficos';
import { useRespostas, useTotaisDeEtapas } from './useRelatorios';
import {
  exportarExcel, formatarDataHora, formatarNota, formatarTelefone, paraData, semAcento,
  statusDoAluno, telefoneDoProgresso, tempoRelativo,
} from './util';

const LIMITE_RESULTADOS = 30;
const RECENTES = 10;
const ultimaAtividade = (aluno) => Math.max(0, ...aluno.cursos.map((p) => paraData(p.timestampUltimaEtapa)?.getTime() || 0));

function RelatorioAluno({ tenantId, minicursos, progressos, nomeDe, telefone, onSelecionar }) {
  const [busca, setBusca] = useState('');

  // Um aluno por telefone, com todos os cursos dele
  const alunos = useMemo(() => {
    const mapa = {};
    progressos.forEach((p) => {
      const tel = telefoneDoProgresso(p);
      if (!mapa[tel]) mapa[tel] = { telefone: tel, contactId: p.contactId, nome: null, cursos: [] };
      mapa[tel].cursos.push(p);
      mapa[tel].nome = mapa[tel].nome || nomeDe(p);
    });
    return Object.values(mapa).sort((a, b) => ultimaAtividade(b) - ultimaAtividade(a));
  }, [progressos, nomeDe]);

  const resultados = useMemo(() => {
    const termo = semAcento(busca.trim());
    if (!termo) return alunos.slice(0, RECENTES);
    const digitos = termo.replace(/\D/g, '');
    return alunos
      .filter((a) => semAcento(a.nome).includes(termo) || (digitos.length >= 3 && a.telefone.includes(digitos)))
      .slice(0, LIMITE_RESULTADOS);
  }, [alunos, busca]);

  const selecionado = telefone ? alunos.find((a) => a.telefone === telefone) : null;

  if (telefone) {
    return selecionado
      ? <FichaDoAluno tenantId={tenantId} aluno={selecionado} minicursos={minicursos} onVoltar={() => onSelecionar(null)} />
      : (
        <div className="qz-empty">
          Nenhum aluno encontrado para este telefone.
          <div><button type="button" className="btn-secondary rel-botao" onClick={() => onSelecionar(null)}>Voltar à busca</button></div>
        </div>
      );
  }

  return (
    <div className="rel-aluno-busca">
      <label className="rel-busca" htmlFor="rel-busca-aluno">
        <FiSearch aria-hidden="true" />
        <input
          id="rel-busca-aluno"
          type="search"
          placeholder="Buscar aluno por nome ou telefone"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          autoComplete="off"
        />
      </label>
      <p className="rel-secao__apoio">
        {busca.trim()
          ? `${resultados.length} ${resultados.length === 1 ? 'aluno encontrado' : 'alunos encontrados'}${resultados.length === LIMITE_RESULTADOS ? ' (refine a busca para ver mais)' : ''}`
          : `Atividade recente · ${alunos.length} alunos no total`}
      </p>

      {resultados.length === 0 ? (
        <p className="qz-empty">Nenhum aluno corresponde à busca.</p>
      ) : (
        <ul className="rel-lista-alunos">
          {resultados.map((a) => (
            <li key={a.telefone}>
              <button type="button" className="rel-lista-alunos__item" onClick={() => onSelecionar(a.telefone)}>
                <span>
                  <span className="rel-lista-alunos__nome">{a.nome || 'Sem nome'}</span>
                  <span className="rel-tabela__suave rel-bloco">{formatarTelefone(a.telefone, a.contactId)}</span>
                </span>
                <span className="rel-lista-alunos__meta">
                  {a.cursos.length} {a.cursos.length === 1 ? 'curso' : 'cursos'} · {tempoRelativo(Math.max(...a.cursos.map((p) => paraData(p.timestampUltimaEtapa)?.getTime() || 0)))}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FichaDoAluno({ tenantId, aluno, minicursos, onVoltar }) {
  const respostas = useRespostas(tenantId, 'telefone', aluno.telefone);
  const totais = useTotaisDeEtapas(aluno.cursos.map((p) => p.minicursoId));
  const nomeDoCurso = (id) => (id ? minicursos.find((m) => m.id === id)?.nome || 'Curso removido' : 'Questões avulsas (fila)');

  const grupos = useMemo(() => {
    if (!respostas) return [];
    const porCurso = {};
    respostas
      .slice()
      .sort((a, b) => (paraData(a.timestamp)?.getTime() || 0) - (paraData(b.timestamp)?.getTime() || 0))
      .forEach((r) => {
        const chave = r.minicursoId || '';
        (porCurso[chave] = porCurso[chave] || []).push(r);
      });
    return Object.entries(porCurso).map(([id, lista]) => ({ id, nome: nomeDoCurso(id || null), lista }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respostas, minicursos]);

  const exportar = () => {
    exportarExcel(`aluno-${aluno.telefone}.xlsx`, [
      {
        nome: 'Cursos',
        linhas: aluno.cursos.map((p) => ({
          Curso: nomeDoCurso(p.minicursoId),
          'Etapas concluídas': p.ultimaEtapaConcluida || 0,
          'Total de etapas': totais[p.minicursoId] ?? '',
          Acertos: p.acertos || 0,
          Erros: p.erros || 0,
          Nota: typeof p.notaFinal === 'number' ? p.notaFinal : '',
          Status: statusDoAluno(p).rotulo,
          Tentativas: p.tentativas || 1,
          Inscrição: formatarDataHora(p.timestampInscricao),
          'Última atividade': formatarDataHora(p.timestampUltimaEtapa),
        })),
      },
      {
        nome: 'Respostas',
        linhas: grupos.flatMap((g) => g.lista.map((r) => ({
          Curso: g.nome,
          Questão: r.enunciado,
          Tipo: r.type === 'quiz' ? 'Quiz' : 'Pesquisa',
          Resposta: r.resposta,
          Gabarito: r.type === 'quiz' ? r.respostacorreta : '',
          Resultado: r.acertou === true ? 'Acertou' : r.acertou === false ? 'Errou' : '',
          Quando: formatarDataHora(r.timestamp),
        }))),
      },
    ]);
  };

  return (
    <div className="rel-ficha">
      <div className="rel-curso__acoes">
        <button type="button" className="btn-ghost rel-botao" onClick={onVoltar}>
          <FiArrowLeft aria-hidden="true" /> Voltar à busca
        </button>
        <button type="button" className="btn-secondary rel-botao" onClick={exportar} disabled={!respostas}>
          <FiDownload aria-hidden="true" /> Exportar Excel
        </button>
      </div>

      <header className="rel-ficha__cabecalho">
        <h2 className="rel-ficha__nome">{aluno.nome || 'Aluno sem nome'}</h2>
        <span className="rel-tabela__suave">{formatarTelefone(aluno.telefone, aluno.contactId)}</span>
      </header>

      <div className="rel-ficha__cursos">
        {aluno.cursos.map((p) => (
          <article key={p.id} className="qz-card rel-ficha__curso">
            <div className="rel-ficha__curso-topo">
              <h3 className="rel-ficha__curso-nome">{nomeDoCurso(p.minicursoId)}</h3>
              <SeloStatus status={statusDoAluno(p)} />
            </div>
            <Medidor valor={p.ultimaEtapaConcluida || 0} total={totais[p.minicursoId] || 0} />
            <dl className="rel-ficha__dados">
              <div><dt>Acertos</dt><dd>{p.acertos || 0}</dd></div>
              <div><dt>Erros</dt><dd>{p.erros || 0}</dd></div>
              <div><dt>Nota</dt><dd>{formatarNota(p.notaFinal)}</dd></div>
              <div><dt>Tentativas</dt><dd>{p.tentativas || 1}</dd></div>
              <div><dt>Inscrição</dt><dd>{formatarDataHora(p.timestampInscricao)}</dd></div>
              <div><dt>Última atividade</dt><dd>{tempoRelativo(p.timestampUltimaEtapa)}</dd></div>
            </dl>
          </article>
        ))}
      </div>

      {!respostas ? (
        <Carregando texto="Carregando respostas..." />
      ) : grupos.length === 0 ? (
        <p className="qz-empty">Este aluno ainda não respondeu nenhuma questão.</p>
      ) : (
        grupos.map((g) => (
          <section key={g.id || 'avulsas'} className="qz-card rel-secao">
            <h2 className="rel-secao__titulo">Respostas · {g.nome}</h2>
            <div className="rel-tabela-rolagem">
              <table className="rel-tabela">
                <thead>
                  <tr>
                    <th scope="col">Quando</th>
                    <th scope="col">Questão</th>
                    <th scope="col">Resposta</th>
                    <th scope="col">Gabarito</th>
                    <th scope="col">Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {g.lista.map((r) => (
                    <tr key={r.id}>
                      <td className="rel-tabela__suave rel-sem-quebra">{formatarDataHora(r.timestamp)}</td>
                      <td className="rel-tabela__texto">{r.enunciado}</td>
                      <td className="rel-tabela__texto">{r.resposta}</td>
                      <td>{r.type === 'quiz' ? r.respostacorreta : '—'}</td>
                      <td>
                        {r.acertou === true && <span className="qz-badge qz-badge--success"><FiCheck aria-hidden="true" />Acertou</span>}
                        {r.acertou === false && <span className="qz-badge qz-badge--danger"><FiX aria-hidden="true" />Errou</span>}
                        {r.acertou !== true && r.acertou !== false && <span className="qz-badge">Pesquisa</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}
    </div>
  );
}

export default RelatorioAluno;
